import { Box } from "@chakra-ui/react";
import { Tabs } from "@/components";
import { useCharge, useSubscription } from "@/hooks";
import {
  Charge,
  ChargePaginator,
  Subscription,
  SubscriptionPaginator,
} from "@/models";
import { ClientData } from "@/models";
import { useEffect, useState } from "react";
import {
  formatCurrency,
  getColorBadgeStatus,
  getPeriodicity,
  getStatus,
  formatDate
} from "@/utils";
import { showToast } from "@/utils";
import { useNavigate } from "react-router-dom";
import { ColumnDefinition, Pills, Table } from "barte-design-system";

interface ChargeHistoryProps {
  client: ClientData;
}

const subscriptionColumns: ColumnDefinition<Subscription>[] = [
  {
    key: "startDate",
    title: "Contratação",
    dataIndex: "startDate",
    render: (startDate) => formatDate(startDate),
  },
  {
    key: "titlePlan",
    title: "Plano",
    dataIndex: "titlePlan",
  },
  {
    key: "type",
    title: "Periodicidade",
    dataIndex: "basicValue",
    render: (basicValue) => getPeriodicity(basicValue?.type),
  },
  {
    key: "value",
    title: "Valor/mês",
    dataIndex: "basicValue",
    render: (basicValue) => formatCurrency({ value: basicValue?.value }),
  },
  {
    key: "status",
    title: "Status",
    dataIndex: "status",
    render: (status) =>
      status ? (
        <Pills label={getStatus(status)} variant="light" state={getColorBadgeStatus(status)} size="sm" />
      ) : (
        "-"
      ),
  },
];

const dsHistoryColumns: ColumnDefinition<Charge>[] = [
  {
    key: "expirationDate",
    title: "Vencimento",
    dataIndex: "expirationDate",
    render: (expirationDate) => formatDate(expirationDate as string),
  },
  {
    key: "title",
    title: "Produto / Serviço",
    dataIndex: "title",
  },
  {
    key: "value",
    title: "Valor/mês",
    dataIndex: "value",
    render: (value) => formatCurrency({ value: value as number }),
  },
  {
    key: "status",
    title: "Status",
    dataIndex: "status",
    render: (status) =>
      status ? (
        <Pills label={getStatus(status as string)} variant="light" state={getColorBadgeStatus(status as string)} size="sm" />
      ) : (
        "-"
      ),
  },
];

const dsScheduledColumns: ColumnDefinition<Charge>[] = [
  {
    key: "expirationDate",
    title: "Vencimento",
    dataIndex: "expirationDate",
    render: (expirationDate) => formatDate(expirationDate as string),
  },
  {
    key: "value",
    title: "Valor/mês",
    dataIndex: "value",
    render: (value) => formatCurrency({ value: value as number }),
  },
  {
    key: "status",
    title: "Status",
    dataIndex: "status",
    render: (status) =>
      status ? (
        <Pills label={getStatus(status as string)} variant="light" state={getColorBadgeStatus(status as string)} size="sm" />
      ) : (
        "-"
      ),
  },
];

const ChargeHistory = ({ client }: ChargeHistoryProps) => {
  const [data, setData] = useState({} as SubscriptionPaginator);
  const [chargeData, setChargeData] = useState({} as ChargePaginator);
  const [getTab, setGetTab] = useState("SUBSCRIPTIONS");
  const [getInternalTabReceived, setGetInternalTabReceived] =
    useState("HISTORY");
  const { getSubscriptions } = useSubscription();
  const { getCharges } = useCharge();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("barte-user-info"));

  const chargeColumns = getInternalTabReceived === "SCHEDULED"
    ? dsScheduledColumns
    : dsHistoryColumns;

  async function getChargesData(type?: string) {
    if (!client || !client?.document) return;
    try {
      if(user?.roles?.includes("SELLER_MASTER") ||
      user?.roles?.includes("SELLER_ADMIN") ||
      user?.roles?.includes("SELLER_BACKOFFICE") ||
      user?.roles?.includes("SELLER_CHARGES")) {
        const response = await getCharges(
          {
            chargeFilterConfig: type ?? "ALL",
            customerDocument: client?.document,
          },
          {
            size: 10000,
            page: 0,
          }
        );
        setChargeData(response.data);
      }
    } catch (error) {
      error?.response?.data?.errors.map((error) => {
        showToast({
          type: "error",
          message: `${error?.code} - ${error?.description}`
        })
      });
    }
  }

  let tabs = [];
  if(user?.roles?.includes("SELLER_MASTER") ||
  user?.roles?.includes("SELLER_ADMIN") ||
  user?.roles?.includes("SELLER_SUBSCRIPTIONS") ||
  user?.roles?.includes("SELLER_BACKOFFICE")) {
    tabs.push({
      tab: "Assinaturas",
      key: "SUBSCRIPTIONS",
      status: "SUBSCRIPTIONS",
    })
  }
  if(user?.roles?.includes("SELLER_MASTER") ||
  user?.roles?.includes("SELLER_ADMIN") ||
  user?.roles?.includes("SELLER_BACKOFFICE") ||
  user?.roles?.includes("SELLER_CHARGES")) {
    tabs.push({
      tab: "Recebíveis",
      key: "RECEIVEDS",
      status: "RECEIVEDS",
    })
  }

  const receivedTabs = [
    {
      tab: "Histórico",
      key: "HISTORY",
      status: "HISTORY",
    },
    {
      tab: "Agendados",
      key: "SCHEDULED",
      status: "SCHEDULED",
    },
  ];

  async function loadData() {
    if (!client || !client?.document) return;
      if(user?.roles?.includes("SELLER_MASTER") ||
      user?.roles?.includes("SELLER_ADMIN") ||
      user?.roles?.includes("SELLER_BACKOFFICE") ||
      user?.roles?.includes("SELLER_SUBSCRIPTIONS")) {
        const { data } = await getSubscriptions(
          { customerDocument: client?.document },
          {
            size: 10000,
            page: 0,
          }
        );
        setData(data);
      }
  }

  useEffect(() => {
    loadData();
    getChargesData();
  }, [client?.document]);

  function handleChangeTab(tabOption: string) {
    setGetTab(tabOption);
  }

  function handleChangeReceivedsTab(tabOption: string) {
    if (tabOption === "HISTORY") {
      getChargesData("ALL");
      setGetInternalTabReceived(tabOption);
      return;
    }
    if (tabOption === "SCHEDULED") {
      getChargesData("SCHEDULED");
      setGetInternalTabReceived(tabOption);
      return;
    }
  }

  function handleChargeRowClick(record: Charge) {
    if (getInternalTabReceived === "HISTORY") {
      navigate(`/cobrancas/${record.uuid}`);
    }
    if (getInternalTabReceived === "SCHEDULED") {
      navigate(`/cobrancas-agendadas/${record.uuid}`);
    }
  }

  const subscriptionsContent = (
    <Box mt="1rem">
      <Table<Subscription>
        columns={subscriptionColumns}
        data={data.content || []}
        rowKey={(record) => record.uuid}
        onRowClick={(record) => navigate(`/recorrencias/${record.uuid}`)}
        emptyText="Sem dados."
      />
    </Box>
  );

  const receivedsContent = (
    <Box mt="1rem">
      <Tabs
        tabs={receivedTabs.map(({ key, tab }) => ({
          key,
          label: tab,
          content: (
            <Box mt="1rem">
              <Table<Charge>
                columns={chargeColumns}
                data={chargeData.content || []}
                rowKey={(record) => record.uuid}
                onRowClick={handleChargeRowClick}
              />
            </Box>
          ),
        }))}
        onChange={handleChangeReceivedsTab}
        currentKey={getInternalTabReceived}
      />
    </Box>
  );

  const mainTabs = tabs.map(({ key, tab }) => ({
    key,
    label: tab,
    content: key === "SUBSCRIPTIONS" ? subscriptionsContent : receivedsContent,
  }));

  return (
    <Box mt="2rem">
      <Tabs
        tabs={mainTabs}
        onChange={handleChangeTab}
        currentKey={getTab}
      />
    </Box>
  );
};

export default ChargeHistory;
